import {
    AppError,
    Container,
    ContainerFactory,
    ContainerRef,
    EntityFactory,
    IContainer,
    InstanceFactory,
    IPageDraft
} from "@amzn/ask-legal-domain";
import {
    Box,
    Button,
    ButtonDropdown,
    ButtonDropdownProps,
    Flashbar,
    FlashbarProps,
    SpaceBetween,
    Spinner,
    Toggle
} from "@amzn/awsui-components-react";
import * as React from "react";
import { AppContext } from "../../setup/context";
import { callApi } from "../../utils/api-call";
import { PageDraftModel } from "../../model/page-draft-model";
import { UIModel } from "../../model/ui-model";
import { ErrorFlashbar } from "../common/ErrorFlashbar";
import { ContainerDiffView } from "./ContainerDiffView";
import { CustomCommonContainerModal } from "./CommonContainerModal";
import { RemoveContainerConfirmModal } from "./modal-content/RemoveContainerConfirmModal";
import { DocumentUploadFileModal } from "./modal-content/DocumentFileUploadModal";
import { ViewContainerContent } from "./modal-content/ViewContainerContent";
import {
    getObjectSizeinBytes,
    getQueryParameterMap,
    isCompareFeatureActivated,
    scrollWithDelayInMs,
    StringUtils,
    UIConstants
} from "../../utils/common-utils";
import { ContainerButtonDropdown } from "./container-expandable-header/ContainerManageModeHeader";
import { ContainerGeneralInfo } from "./ContainerGeneralInfo";
import { CopyContainerModal } from "./CopyContainerModal";
import { EditContainerContent } from "./modal-content/EditContainerContent";
import { S3ExportSettingsModal } from "./modal-content/S3ExportSettingsModal";
import { useNavigate } from "react-router-dom";
import { getContainerHeader } from "../../utils/container-utils";
import {
    BackToTopButtonV2,
    CardInfoPopoverV2,
    ContainerCardV2,
    ContainerLinkCopyButtonV2
} from "./ContainerCardV2";

/**
 * Draft-editor container for the PathFinder Agentic UI lab. Same behavior as ContainerEdit
 * (edit, delete, reorder, upload, decision tree, S3 export, copy, compare, `?container=&edit=true`);
 * only the shell is the V2 card shared with the live page.
 */
export namespace ContainerEditV2 {
    export const Preload = (props: {
        id: string;
        activeEditContainerId: UIModel.State<string>;
        updateOrderState: PageDraftModel.ReorderContainerState;
        liveContainerRef?: ContainerRef;
        onLoadComplete?: (loadedData: IContainer.LoadContainerOutput) => void;
    }) => {
        const context = React.useContext(AppContext);
        const [loadStatus, setLoadStatus] = React.useState<"loading" | "error" | "loaded">("loading");
        const containerDataState = UIModel.State.use<IContainer.LoadContainerOutput>({
            initialValue: null
        });
        // Only the latest load may update state (the id can change, and reload can overlap)
        const latestLoadRef = React.useRef(0);

        const loadContainer = () => {
            const loadId = ++latestLoadRef.current;
            setLoadStatus("loading");
            callApi(context.getContainerAPI().load, props.id).then(result => {
                if (loadId !== latestLoadRef.current) return;
                if (!result.ok) {
                    setLoadStatus("error");
                    return;
                }
                const loaded = result.data;
                if (
                    props.liveContainerRef?.version === loaded.containerMetadata?.version &&
                    props.liveContainerRef?.containerType === Container.Type.ADVANCED_LIST
                ) {
                    containerDataState.setValue({
                        ...loaded,
                        loadedContent: {
                            ...loaded.loadedContent,
                            updateAdvancedListPayload: {},
                            upsertWorkflowList: [],
                            deleteWorkflowList: undefined,
                            upsertViewsList: [],
                            deleteViewsList: undefined,
                        }
                    });
                } else {
                    containerDataState.setValue(loaded);
                }
                setLoadStatus("loaded");
                if (!props.onLoadComplete) return;
                props.onLoadComplete(loaded);
            });
        };

        React.useEffect(() => {
            loadContainer();
        }, [props.id]);

        React.useEffect(() => {
            // Ignore responses that arrive after unmount
            return () => { latestLoadRef.current++; };
        }, []);

        const statusBodyId = `pf-card-body-${props.id}`;

        return (
            <React.Fragment>
                {loadStatus === "loaded" && !!containerDataState.value &&
                    <Comp
                        state={containerDataState}
                        reload={() => {
                            containerDataState.setValue(null);
                            loadContainer();
                        }}
                        activeEditContainerId={props.activeEditContainerId}
                        updateOrderState={props.updateOrderState}
                        liveContainerRef={props.liveContainerRef}
                    />
                }
                {loadStatus === "error" &&
                    <ContainerCardV2 title="Error Loading Container" bodyId={statusBodyId}>
                        Error Loading Container
                    </ContainerCardV2>
                }
                {loadStatus === "loading" &&
                    <ContainerCardV2 title="Loading..." bodyId={statusBodyId}>
                        <Spinner />
                    </ContainerCardV2>
                }
            </React.Fragment>
        );
    };

    export const Comp = (props: {
        state: UIModel.State<IContainer.LoadContainerOutput>;
        updateOrderState: PageDraftModel.ReorderContainerState;
        activeEditContainerId: UIModel.State<string>;
        liveContainerRef?: ContainerRef;
        reload: () => void;
    }) => {
        const context = React.useContext(AppContext);
        const navigate = useNavigate();
        const [deleting, setDeleting] = React.useState<boolean>(false);
        const [expanded, setExpanded] = React.useState<boolean>(true);
        const showDiffState = UIModel.State.use<boolean>({ initialValue: false });
        const [liveContainerData, setLiveContainerData] = React.useState<IContainer.LoadContainerOutput>();
        const [containerStatus, setContainerStatus] = React.useState<FlashbarProps.MessageDefinition[]>([]);

        const [customSize, setCustomSize] = React.useState<"sm" | "lg" | "xl" | "xxl">("lg");
        const [fileUploading, setFileUploading] = React.useState<boolean>(false);
        const [showS3ExportModal, setShowS3ExportModal] = React.useState<boolean>(false);
        const [showCopyModal, setShowCopyModal] = React.useState<boolean>(false);

        const editContainerState = PageDraftModel.EditContainerState.use({
            draftRef: EntityFactory.toEntityRef(
                props.updateOrderState.draftField.value
            )
        });

        const [isSavingEdit, setIsSavingEdit] = React.useState<boolean>(false);
        const [editError, setEditError] = React.useState<AppError>();
        const [isRemoving, setIsRemoving] = React.useState<boolean>(false);
        const [removeError, setRemoveError] = React.useState<AppError>();
        const [isLoadingLiveVersion, setIsLoadingLiveVersion] = React.useState<boolean>(false);
        const isMountedRef = React.useRef<boolean>(true);

        React.useEffect(() => {
            isMountedRef.current = true;
            return () => { isMountedRef.current = false; };
        }, []);

        const edit = () => {
            const editPayload = PageDraftModel.EditContainerState.toEditContainerInput(
                editContainerState
            );
            const payloadSize = getObjectSizeinBytes(editPayload);
            if (payloadSize > UIConstants.MAX_EDIT_CONTAINER_SIZE_MB * 1024 * 1024) {
                setContainerStatus([
                    {
                        type: "error",
                        header: `Container size limit exceeded [ ${StringUtils.prettyPrintDecimal(payloadSize / (1024 * 1024))} MB / ${UIConstants.MAX_EDIT_CONTAINER_SIZE_MB} MB ]`,
                        content: `Learn how to resolve this issue here: ${UIConstants.MAX_EDIT_CONTAINER_LIMIT_DOCUMENTATION}`,
                        dismissible: true,
                        onDismiss: () => {
                            setContainerStatus([]);
                        }
                    }
                ]);
                return;
            }
            setContainerStatus([]);
            submitEdit(editPayload);
            navigator.clipboard?.writeText("").catch(() => undefined);
        };

        const submitEdit = async (editPayload: IPageDraft.EditContainerInput) => {
            setIsSavingEdit(true);
            setEditError(undefined);
            const result = await callApi(context.getPageDraftAPI().editContainer, editPayload);
            if (!isMountedRef.current) return;
            setIsSavingEdit(false);
            if (!result.ok) {
                setEditError(result.err);
                return;
            }
            editContainerState.init(null);
            props.updateOrderState.setDraft(result.data);
            props.reload();
            props.activeEditContainerId.setValue("");
        };

        const remove = async () => {
            setIsRemoving(true);
            setRemoveError(undefined);
            const result = await callApi(
                context.getPageDraftAPI().removeContainer,
                IPageDraft.RemoveContainerInput.create({
                    containerRef: ContainerFactory.toContainerRef(props.state.value.containerMetadata),
                    draftRef: EntityFactory.toEntityRef(props.updateOrderState.draftField.value)
                })
            );
            if (!isMountedRef.current) return;
            setIsRemoving(false);
            if (!result.ok) {
                setRemoveError(result.err);
                return;
            }
            setDeleting(false);
            props.updateOrderState.setDraft(result.data);
        };

        const clearURLEditMode = () => {
            let newWindowhash = window.location.hash
                .split("&")
                .filter((x) => !x.includes("edit=true"))
                .join("&");
            if (newWindowhash.startsWith("#")) {
                newWindowhash = newWindowhash.slice(1);
            }
            navigate(newWindowhash);
        };

        const cancelEdit = () => {
            editContainerState.init(null);
            setFileUploading(false);
            navigator.clipboard?.writeText("").catch(() => undefined);
            props.activeEditContainerId.setValue("");
        };

        const buttonDropdownEventHandler = (buttonId: string) => {
            switch (buttonId) {
                case ContainerButtonDropdown.Edit.id:
                    if (props.liveContainerRef?.version === props.state.value?.containerMetadata?.version &&
                        props.state.value?.containerMetadata?.containerType === Container.Type.ADVANCED_LIST
                    ) {
                        editContainerState.init({
                            container: props.state.value.containerMetadata,
                            containerContent: {
                                ...props.state.value.loadedContent,
                                updateAdvancedListPayload: {},
                                upsertWorkflowList: [],
                                deleteWorkflowList: undefined
                            }
                        });
                    } else {
                        editContainerState.init({
                            container: props.state.value.containerMetadata,
                            containerContent: props.state.value.loadedContent
                        });
                    }

                    props.activeEditContainerId.setValue(
                        props.state.value.containerMetadata.id
                    );
                    break;
                case ContainerButtonDropdown.Delete.id:
                    setDeleting(true);
                    break;
                case ContainerButtonDropdown.AddGroup.id:
                    alert("Not Implemented");
                    break;
                case ContainerButtonDropdown.AddQuestion.id:
                    alert("Not Implemented");
                    break;
                case ContainerButtonDropdown.Moveup.id:
                    props.updateOrderState.moveUp(
                        props.state.value.containerMetadata
                    );
                    break;
                case ContainerButtonDropdown.Movedown.id:
                    props.updateOrderState.moveDown(
                        props.state.value.containerMetadata
                    );
                    break;
                case ContainerButtonDropdown.Upload.id:
                    // Mechanism to open upload file modal
                    editContainerState.init({
                        container: props.state.value.containerMetadata,
                        containerContent: props.state.value.loadedContent
                    });
                    setFileUploading(true);
                    break;
                case ContainerButtonDropdown.EditDecisionTree.id:
                    // Redirect to Decision Tree Editor
                    navigate(`/decision-tree/${props.state.value.containerMetadata.id
                        }/edit?draftId=${props.updateOrderState.draftField.value.id
                        }&draftVersion=${props.updateOrderState.draftField.value.version
                        }`);
                    break;
                case ContainerButtonDropdown.S3ExportSettings.id:
                    setShowS3ExportModal(true);
                    break;
                case ContainerButtonDropdown.CopyToPage.id:
                    setShowCopyModal(true);
                    break;
            }
        };

        React.useEffect(() => {
            if (
                !!props.liveContainerRef &&
                props.liveContainerRef.version !== props.state.value.containerMetadata.version &&
                isCompareFeatureActivated(props.state.value.containerMetadata.containerType) // Control Diff by container type
            ) {
                setIsLoadingLiveVersion(true);
                callApi(context.getContainerAPI().loadVersion, props.liveContainerRef).then(result => {
                    if (!isMountedRef.current) return;
                    setIsLoadingLiveVersion(false);
                    if (result.ok) {
                        setLiveContainerData(result.data);
                    }
                });
            }
        }, []);

        React.useEffect(() => {
            if (!editContainerState || !editContainerState.activeContainerField.value) return;
            switch (editContainerState.activeContainerField.value.containerType) {
                default: setCustomSize("xxl");
            }
        }, [editContainerState.activeContainerField.value]);

        React.useEffect(() => {
            const queryParamMap = getQueryParameterMap();
            if (queryParamMap.get("container")) {
                const targetContainerId = queryParamMap.get("container");
                if (props.state.value.containerMetadata.id !== targetContainerId) return;
                if (queryParamMap.get("edit") === "true") {
                    editContainerState.init({
                        container: props.state.value.containerMetadata,
                        containerContent: props.state.value.loadedContent
                    });
                    props.activeEditContainerId.setValue(
                        props.state.value.containerMetadata.id);
                    clearURLEditMode();
                }
                scrollWithDelayInMs(700, targetContainerId);
            }
        }, []);

        const containerMetadata = props.state.value.containerMetadata;

        const isEditingCurrentContainer = () => {
            return props.activeEditContainerId.value === containerMetadata.id;
        };

        const isToolsContainer = () => {
            return containerMetadata.containerType === Container.Type.LIST;
        };

        const isEditing = () => {
            return !StringUtils.isEmpty(props.activeEditContainerId.value);
        };

        const isManageMode = !isEditing() && props.updateOrderState.draftField.value.status === "DRAFT";
        const showCompareToggle = isManageMode &&
            (!props.liveContainerRef || props.liveContainerRef.version !== containerMetadata.version) &&
            isCompareFeatureActivated(containerMetadata.containerType) &&
            !isLoadingLiveVersion;

        const getActionsMenuItems = (): ButtonDropdownProps.Items => {
            let items: ButtonDropdownProps.Items = [];
            switch (containerMetadata.containerType) {
                case Container.Type.RICH_TEXT:
                case Container.Type.ACCORDION:
                case Container.Type.ACCORDION_GROUP:
                case Container.Type.LIST:
                case Container.Type.ADVANCED_LIST:
                    items = [ContainerButtonDropdown.Edit, ContainerButtonDropdown.Reorder, ContainerButtonDropdown.Delete];
                    break;
                case Container.Type.DOCUMENT:
                    items = [
                        ContainerButtonDropdown.Upload,
                        ContainerButtonDropdown.Edit,
                        ContainerButtonDropdown.Reorder,
                        ContainerButtonDropdown.Delete
                    ];
                    break;
                case Container.Type.DECISION_TREE:
                    items = [
                        ContainerButtonDropdown.Edit,
                        ContainerButtonDropdown.EditDecisionTree,
                        ContainerButtonDropdown.Reorder,
                        ContainerButtonDropdown.Delete
                    ];
                    break;
                case Container.Type.WHOS_MY_LAWYER:
                    items = [
                        ContainerButtonDropdown.Edit,
                        ContainerButtonDropdown.S3ExportSettings,
                        ContainerButtonDropdown.Reorder,
                        ContainerButtonDropdown.Delete
                    ];
                    break;
                default:
                    items = [];
            }
            // Add "Copy to Page" to all container types, just before Delete
            if (items.length > 0) {
                const deleteIndex = items.findIndex(i => i.id === "delete");
                if (deleteIndex !== -1) {
                    items = [...items.slice(0, deleteIndex), ContainerButtonDropdown.CopyToPage, ...items.slice(deleteIndex)];
                } else {
                    items = [...items, ContainerButtonDropdown.CopyToPage];
                }
            }
            return items;
        };

        const headerActions = (
            <>
                {showCompareToggle && (
                    <Toggle
                        onChange={({ detail }) => showDiffState.setValue(detail.checked)}
                        checked={showDiffState.value}
                    >
                        Compare with live
                    </Toggle>
                )}
                {isManageMode && isLoadingLiveVersion && <Spinner />}
                <ContainerLinkCopyButtonV2 containerId={containerMetadata.id} />
                <BackToTopButtonV2 />
                {isManageMode && (
                    <ButtonDropdown
                        variant="icon"
                        ariaLabel="Container actions"
                        loading={isSavingEdit}
                        items={getActionsMenuItems()}
                        onItemClick={(event) => buttonDropdownEventHandler(event.detail.id)}
                    />
                )}
            </>
        );

        const renderBody = () => {
            if (isEditingCurrentContainer()) {
                if (isToolsContainer()) {
                    return (
                        <CustomCommonContainerModal
                            header={getContainerHeader("Edit", editContainerState.activeContainerField.value.containerType)}
                            child={
                                <SpaceBetween size="xs" direction="vertical">
                                    <Flashbar items={containerStatus} />
                                    <EditContainerContent state={editContainerState} />
                                </SpaceBetween>
                            }
                            onSave={edit}
                            loading={isSavingEdit || isRemoving}
                            onCancel={() => {
                                cancelEdit();
                                setContainerStatus([]);
                            }}
                            size={customSize}
                            disabled={!editContainerState.validState}
                        />
                    );
                }
                return (
                    <SpaceBetween size="m">
                        <Flashbar items={containerStatus} />
                        <EditContainerContent state={editContainerState} />
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button
                                    variant="link"
                                    loading={isSavingEdit}
                                    onClick={() => {
                                        cancelEdit();
                                        setContainerStatus([]);
                                    }}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    loading={isSavingEdit}
                                    onClick={edit}
                                    disabled={!editContainerState.validState}>
                                    Submit
                                </Button>
                            </SpaceBetween>
                        </Box>
                    </SpaceBetween>
                );
            }
            if (showDiffState.value) {
                return (
                    <ContainerDiffView
                        newContainer={props.state.value}
                        oldContainer={liveContainerData}
                    />
                );
            }
            return (
                <ViewContainerContent
                    containerMetadata={containerMetadata}
                    content={props.state.value.loadedContent}
                    showEditControls={true}
                    liveContainerRef={props.liveContainerRef}
                />
            );
        };

        return (
            <React.Fragment>
                {!!editError && <ErrorFlashbar error={editError} />}
                {!!removeError && <ErrorFlashbar error={removeError} />}
                <ContainerCardV2
                    title={containerMetadata.title}
                    bodyId={`pf-card-body-${containerMetadata.id}`}
                    expanded={expanded}
                    onToggle={() => setExpanded(!expanded)}
                    headerExtras={
                        <CardInfoPopoverV2
                            header="Container Info"
                            content={<ContainerGeneralInfo data={containerMetadata} />}
                        />
                    }
                    actions={headerActions}
                >
                    {renderBody()}
                </ContainerCardV2>
                {(!!editContainerState.activeContainerField.value && !!fileUploading) && (
                    <DocumentUploadFileModal
                        containerId={containerMetadata.id}
                        draftRef={EntityFactory.toEntityRef(props.updateOrderState.draftField.value)}
                        loading={isSavingEdit || isRemoving}
                        onClose={() => {
                            props.reload();
                            editContainerState.init(null);
                            setFileUploading(false);
                            navigator.clipboard?.writeText("").catch(() => undefined);
                        }}
                    />
                )}
                {deleting &&
                    <RemoveContainerConfirmModal
                        container={containerMetadata}
                        visible={deleting}
                        loading={isRemoving || isSavingEdit}
                        onDismiss={() => setDeleting(false)}
                        onDelete={remove}
                    />
                }
                {showS3ExportModal &&
                    <S3ExportSettingsModal
                        containerMetadata={containerMetadata}
                        visible={showS3ExportModal}
                        onDismiss={() => setShowS3ExportModal(false)}
                        onSaved={() => {
                            props.reload();
                        }}
                    />
                }
                {showCopyModal &&
                    <CopyContainerModal
                        visible={showCopyModal}
                        onDismiss={() => setShowCopyModal(false)}
                        sourceContainerId={containerMetadata.id}
                        sourceSection={
                            props.updateOrderState.draftField.value.newSections
                                .find(s => s.containers.some(c => c.id === containerMetadata.id))
                                ?.location || "Main"
                        }
                        instanceId={InstanceFactory.fromEntityId(containerMetadata.id)}
                    />
                }
            </React.Fragment>
        );
    };
}
